const TARGET_TOP_GAP = 12;
const VIEWPORT_SIDE_GAP = 12;
const FALLBACK_HEADER_HEIGHT = 64;
const ADJUSTMENT_DELAYS = [0, 120, 360] as const;
const LARGE_TARGET_MIN_WIDTH_RATIO = 0.55;
const LARGE_TARGET_MIN_HEIGHT_RATIO = 0.58;
const VERY_LARGE_TARGET_HEIGHT_RATIO = 0.8;

let adjustmentId = 0;

type OnboardingStepLike = {
  element?: string | Element | (() => Element);
  popover?: Record<string, unknown>;
};

const isVisible = (element: HTMLElement) => {
  const rect = element.getBoundingClientRect();
  const style = window.getComputedStyle(element);

  return rect.width > 0 && rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden';
};

const getHeaderBottom = () => {
  const platformHeader = document.querySelector<HTMLElement>(
    '.layout__root--platform > .header__background',
  );

  if (platformHeader && isVisible(platformHeader)) {
    return Math.max(FALLBACK_HEADER_HEIGHT, platformHeader.getBoundingClientRect().bottom);
  }

  const topBars = Array.from(
    document.querySelectorAll<HTMLElement>('header, [role="banner"], .header__background'),
  );

  const detectedBottom = topBars.reduce((bottom, element) => {
    if (!isVisible(element)) return bottom;

    const rect = element.getBoundingClientRect();
    const position = window.getComputedStyle(element).position;
    const isTopBar =
      (position === 'fixed' || position === 'sticky') &&
      rect.top <= TARGET_TOP_GAP &&
      rect.bottom > 0 &&
      rect.width >= window.innerWidth * 0.5;

    return isTopBar ? Math.max(bottom, rect.bottom) : bottom;
  }, 0);

  return Math.max(FALLBACK_HEADER_HEIGHT, detectedBottom);
};

const getVisibleTarget = (element?: OnboardingStepLike['element']): Element | null => {
  if (!element || typeof document === 'undefined') return null;

  if (typeof element === 'string') {
    return Array.from(document.querySelectorAll(element)).find((candidate) => {
      return candidate instanceof HTMLElement && isVisible(candidate);
    }) ?? null;
  }

  return element instanceof Element ? element : null;
};

const isLargeViewportTarget = (element: Element) => {
  const visualViewport = window.visualViewport;
  const viewportWidth = visualViewport?.width ?? window.innerWidth;
  const viewportHeight = visualViewport?.height ?? window.innerHeight;
  const viewportTop = visualViewport?.offsetTop ?? 0;
  const usableHeight = Math.max(1, viewportHeight - Math.max(0, getHeaderBottom() - viewportTop));
  const rect = element.getBoundingClientRect();
  const widthRatio = rect.width / Math.max(1, viewportWidth);
  const heightRatio = rect.height / usableHeight;

  return (
    heightRatio >= VERY_LARGE_TARGET_HEIGHT_RATIO ||
    (widthRatio >= LARGE_TARGET_MIN_WIDTH_RATIO && heightRatio >= LARGE_TARGET_MIN_HEIGHT_RATIO)
  );
};

/**
 * Large containers leave Driver.js without enough space for its popover and
 * trigger aggressive scrollIntoView calls. Render those overview steps as
 * Driver's native centered popover without an element instead.
 */
export const makeOnboardingStepsViewportSafe = <T extends OnboardingStepLike>(steps: readonly T[]): T[] => {
  if (typeof window === 'undefined') return [...steps];

  return steps.map((step) => {
    const target = getVisibleTarget(step.element);
    if (!target || !isLargeViewportTarget(target)) return step;

    const safeStep: OnboardingStepLike = { ...step };
    const safePopover = { ...(step.popover ?? {}) };

    delete safeStep.element;
    delete safePopover.side;
    delete safePopover.align;

    return {
      ...safeStep,
      popover: safePopover,
    } as T;
  });
};

const getScrollContainer = (element: HTMLElement): HTMLElement | null => {
  let parent = element.parentElement;

  while (parent) {
    const { overflowY } = window.getComputedStyle(parent);
    const canScroll = /(auto|scroll)/.test(overflowY) && parent.scrollHeight > parent.clientHeight;

    if (canScroll) return parent;
    parent = parent.parentElement;
  }

  return document.querySelector<HTMLElement>('.layout__platform-content');
};

const moveTargetBelowHeader = (element: HTMLElement) => {
  const safeTop = getHeaderBottom() + TARGET_TOP_GAP;
  const targetTop = element.getBoundingClientRect().top;

  if (targetTop >= safeTop) return;

  const offset = targetTop - safeTop;
  const scrollContainer = getScrollContainer(element);

  if (scrollContainer) {
    scrollContainer.scrollBy({ top: offset, left: 0, behavior: 'auto' });
    return;
  }

  window.scrollBy({ top: offset, left: 0, behavior: 'auto' });
};

const keepPopoverInsideViewport = () => {
  const popover = document.querySelector<HTMLElement>('.driver-popover');
  if (!popover || !isVisible(popover)) return;

  const visualViewport = window.visualViewport;
  const viewportTop = visualViewport?.offsetTop ?? 0;
  const viewportLeft = visualViewport?.offsetLeft ?? 0;
  const viewportHeight = visualViewport?.height ?? window.innerHeight;
  const viewportWidth = visualViewport?.width ?? window.innerWidth;
  const viewportBottom = viewportTop + viewportHeight;
  const viewportRight = viewportLeft + viewportWidth;
  const safeTop = Math.max(viewportTop + VIEWPORT_SIDE_GAP, getHeaderBottom() + TARGET_TOP_GAP);
  const rect = popover.getBoundingClientRect();
  const maxTop = Math.max(safeTop, viewportBottom - rect.height - VIEWPORT_SIDE_GAP);
  const minLeft = viewportLeft + VIEWPORT_SIDE_GAP;
  const maxLeft = Math.max(minLeft, viewportRight - rect.width - VIEWPORT_SIDE_GAP);
  const nextTop = Math.min(Math.max(rect.top, safeTop), maxTop);
  const nextLeft = Math.min(Math.max(rect.left, minLeft), maxLeft);
  const currentTop = Number.parseFloat(popover.style.top);
  const currentLeft = Number.parseFloat(popover.style.left);

  if (Math.abs(nextTop - rect.top) > 0.5) {
    popover.style.top = `${(Number.isFinite(currentTop) ? currentTop : popover.offsetTop) + nextTop - rect.top}px`;
  }

  if (Math.abs(nextLeft - rect.left) > 0.5) {
    popover.style.right = 'auto';
    popover.style.left = `${(Number.isFinite(currentLeft) ? currentLeft : popover.offsetLeft) + nextLeft - rect.left}px`;
  }
};

/**
 * Driver.js can center a large highlighted block and place its popover outside
 * the usable viewport. Correct both positions after Driver's animated scroll.
 */
export const keepOnboardingTargetVisible = (element?: Element | null) => {
  if (typeof window === 'undefined' || !(element instanceof HTMLElement)) {
    return;
  }

  const currentAdjustmentId = ++adjustmentId;

  const adjust = () => {
    if (currentAdjustmentId !== adjustmentId || !element.isConnected) return;
    moveTargetBelowHeader(element);
    keepPopoverInsideViewport();
  };

  for (const delay of ADJUSTMENT_DELAYS) {
    if (delay === 0) {
      window.requestAnimationFrame(() => window.requestAnimationFrame(adjust));
    } else {
      window.setTimeout(adjust, delay);
    }
  }
};
